import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

import { useBillingNoteText } from '@/billing/hooks/useBillingNoteText';
import { useIsLockedByOtherUser } from '@/billing/hooks/useIsLockedByOtherUser';
import { Checkbox } from '@/common/components/ui/checkbox';
import { TableCell, TableRow } from '@/common/components/ui/table';
import { formatDate } from '@/common/utils/DateFormatters';

import { BillingSummary } from '../api/FetchBillingList';
import { BillingStatusBadge } from './BillingStatusBadge';

interface BillingRowProps {
  billing: BillingSummary;
  selectedBillings: string[];
  setSelectedBillings: Dispatch<SetStateAction<string[]>>;
  onRowClick: (billing: BillingSummary) => void;
}

export const BillingRow = ({
  billing,
  selectedBillings,
  setSelectedBillings,
  onRowClick,
}: BillingRowProps) => {
  const { t, i18n } = useTranslation();
  const isLockedByOtherUser = useIsLockedByOtherUser(billing);
  const latestNote = useBillingNoteText(billing.latestNote);

  const shouldGreyOut = isLockedByOtherUser && billing.status !== 'COMPLETED';

  const handleCheckboxChange = (checked: boolean) => {
    if (checked) {
      const newSelectedBillings = [...selectedBillings, billing.uuid];
      setSelectedBillings(newSelectedBillings);
    } else {
      const newSelectedBillings = selectedBillings.filter(
        (uuid) => uuid !== billing.uuid,
      );
      setSelectedBillings(newSelectedBillings);
    }
  };

  return (
    <TableRow
      className="cursor-pointer hover:bg-muted/50"
      onClick={() => onRowClick(billing)}
    >
      <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
        <Checkbox
          checked={selectedBillings.includes(billing.uuid)}
          onCheckedChange={handleCheckboxChange}
        />
      </TableCell>
      <TableCell>
        <BillingStatusBadge status={billing.status} greyedOut={shouldGreyOut} />
      </TableCell>
      <TableCell>
        {billing.createdAt
          ? formatDate(billing.createdAt, i18n.language)
          : null}
      </TableCell>

      <TableCell>
        <div className="truncate max-w-85" title={latestNote}>
          {latestNote || '-'}
        </div>
      </TableCell>
      <TableCell>
        <div className="truncate" title={billing.searchCode || ''}>
          {billing.searchCode || '-'}
        </div>
      </TableCell>
      <TableCell>
        <div className="truncate" title={billing.insurer}>
          {billing.insurer}
        </div>
      </TableCell>
      <TableCell>
        <div className="truncate" title={billing.activityCode || ''}>
          {billing.activityCode || '-'}
        </div>
      </TableCell>
      <TableCell>{billing.policyNumber}</TableCell>
      <TableCell>
        {billing.effectiveDate
          ? formatDate(billing.effectiveDate, i18n.language)
          : null}
      </TableCell>
      <TableCell>
        {billing.expiryDate
          ? formatDate(billing.expiryDate, i18n.language)
          : null}
      </TableCell>
      <TableCell>{billing.gc1 || '-'}</TableCell>
      <TableCell>
        {billing.isAgencyBill ? t('billing.agency') : t('billing.direct')}
      </TableCell>
      <TableCell>
        <div className="truncate" title={billing.technicalSupport || ''}>
          {billing.technicalSupport || '-'}
        </div>
      </TableCell>
      <TableCell>
        {billing.completedDate
          ? formatDate(billing.completedDate, i18n.language)
          : null}
      </TableCell>
      <TableCell>
        <div
          className="truncate"
          title={billing.assignedUserName || billing.assignedUserId || ''}
        >
          {billing.assignedUserName || billing.assignedUserId || '-'}
        </div>
      </TableCell>
      <TableCell>
        <div className="truncate" title={billing.service || ''}>
          {billing.service || '-'}
        </div>
      </TableCell>

      <TableCell>
        <div className="truncate" title={billing.policyType || ''}>
          {billing.policyType || '-'}
        </div>
      </TableCell>
    </TableRow>
  );
};
